import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { anyPass, isEmpty, isNil, keys, pick, reject } from 'ramda'

import { Page } from '../utils/enums'
import {
  InvoiceFilter,
  invoiceFilterDefaults,
  InvoiceFilters,
} from './invoices.filters'
import {
  OrderFilter,
  orderFilterDefaults,
  OrderFilters,
} from './orders.filters'
import {
  ProductFilter,
  productFilterDefaults,
  ProductFilters,
} from './products.filters'
import {
  QuoteFilter,
  quoteFilterDefaults,
  QuoteFilters,
} from './quotes.filters'
import { UserFilter, userFilterDefaults, UserFilters } from './users.filters'
import { FilterMap } from './utils'

export type FilterState = {
  isFiltersOpen: boolean
  isInitialised?: boolean
  [Page.Invoices]: FilterMap<InvoiceFilters>
  [Page.Orders]: FilterMap<OrderFilters>
  [Page.Products]: FilterMap<ProductFilters>
  [Page.Users]: FilterMap<UserFilters>
  [Page.Quotes]: FilterMap<QuoteFilters>
}

export const initialFilterState: FilterState = {
  [Page.Invoices]: invoiceFilterDefaults,
  [Page.Orders]: orderFilterDefaults,
  [Page.Products]: productFilterDefaults,
  [Page.Users]: userFilterDefaults,
  [Page.Quotes]: quoteFilterDefaults,
  isFiltersOpen: true,
}

const ValidFilters: Record<FilterEndpoint, FilterMap> = {
  [Page.Invoices]: InvoiceFilter,
  [Page.Orders]: OrderFilter,
  [Page.Products]: ProductFilter,
  [Page.Users]: UserFilter,
  [Page.Quotes]: QuoteFilter,
}

export type FilterEndpoint =
  | Page.Orders
  | Page.Products
  | Page.Invoices
  | Page.Users
  | Page.Quotes

export type Filters =
  | ProductFilters
  | InvoiceFilters
  | OrderFilters
  | UserFilters

export const removeInvalidFilters = (endpoint: FilterEndpoint) =>
  pick([...keys(ValidFilters[endpoint]), 'page'])

export const filterEmptyValues = reject(anyPass([isNil, isEmpty]))

export const filtersSlice = createSlice({
  initialState: initialFilterState,
  name: 'filters',
  reducers: {
    resetFiltersAction: (state: FilterState) => ({
      ...initialFilterState,
      isFiltersOpen: state.isFiltersOpen,
      isInitialised: state.isInitialised,
    }),

    toggleSidebarFiltersAction(state: FilterState) {
      state.isFiltersOpen = !state.isFiltersOpen
    },

    updateFiltersAction(
      state: FilterState,
      {
        payload: { endpoint, filters },
      }: PayloadAction<{
        endpoint: FilterEndpoint
        filters: FilterMap
      }>,
    ) {
      state[endpoint] = removeInvalidFilters(endpoint)(
        filterEmptyValues(filters),
      )
      state.isInitialised = true
    },

    updateSearchFilterAction(
      state: FilterState,
      { payload }: PayloadAction<string | undefined>,
    ) {
      if (payload) {
        ;(Object.keys(ValidFilters) as FilterEndpoint[]).forEach(page => {
          state[page].searchQuery = payload
          state[page].page = '1'
          state[page].sort = 'relevance'
        })
      } else {
        ;(Object.keys(ValidFilters) as FilterEndpoint[]).forEach(page => {
          state[page] = initialFilterState[page]
        })
      }
    },
    updateSingleFilterAction(
      state: FilterState,
      {
        payload: { endpoint, filter, value },
      }: PayloadAction<{
        endpoint: FilterEndpoint
        filter: keyof FilterState[FilterEndpoint]
        value: string
      }>,
    ) {
      state[endpoint][filter] = value
    },
  },
})

export default filtersSlice.reducer
